"use client";

import { FC, useEffect, useMemo, useRef, useState } from "react";
import { CldImage } from "next-cloudinary";
import { getDocumentPageCountAction } from "@/app/_lib/actions/document.actions";

type Props = {
  src: string;
  alt: string;
  width: number;
  height: number;
  crop?: "fill" | "fit" | "limit" | "scale" | "crop" | "thumb" | "pad";
  sizes?: string;
  className?: string;
  onClick?: () => void;
  // Keep the arrows/page badge visible instead of revealing them on hover.
  alwaysShowControls?: boolean;
  // Extra classes for the relative wrapper (e.g. "w-full" when the image
  // itself is w-full — the default inline-block shrink-wrap would fight it).
  containerClassName?: string;
};

// The preview is a quick look, not a reader: only the first few pages are
// flippable (and preloaded); longer docs get a "+N more pages" indicator.
const MAX_PREVIEW_PAGES = 5;

// Page counts are immutable per asset, so cache them for the session — the
// same document often renders as a grid thumb and again in the view modal.
const pageCountCache = new Map<string, number>();
const pendingLookups = new Map<string, Promise<number>>();

const fetchPageCount = (src: string): Promise<number> => {
  const cached = pageCountCache.get(src);
  if (cached !== undefined) return Promise.resolve(cached);

  let pending = pendingLookups.get(src);
  if (!pending) {
    pending = getDocumentPageCountAction({ src }).then(([error, result]) => {
      pendingLookups.delete(src);
      if (error || !result) {
        // Don't cache failures — a transient error (hot reload, network
        // blip) would otherwise pin the doc to "single page" all session.
        console.error("Page count lookup failed:", error?.message, src);
        return 1;
      }
      pageCountCache.set(src, result.pages);
      return result.pages;
    });
    pendingLookups.set(src, pending);
  }
  return pending;
};

/**
 * CldImage wrapper that lets users flip through the pages of multi-page
 * documents (PDFs) via arrows overlaid on the preview. Each page is
 * rasterized through Cloudinary's pg_N transformation. All pages stay
 * mounted (hidden) so flipping toggles visibility instead of reloading —
 * if a page is still loading, the previous one stays visible under a
 * spinner. Single-page assets render exactly like a plain CldImage.
 */
const PagedCloudImage: FC<Props> = ({
  src,
  alt,
  width,
  height,
  crop,
  sizes,
  className,
  onClick,
  alwaysShowControls = false,
  containerClassName = "",
}) => {
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(() => pageCountCache.get(src) ?? 1);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set());
  const lastLoadedPageRef = useRef(1);

  useEffect(() => {
    setPage(1);
    setPageCount(pageCountCache.get(src) ?? 1);
    setLoadedPages(new Set());
    lastLoadedPageRef.current = 1;

    let live = true;
    fetchPageCount(src).then((pages) => {
      if (live) setPageCount(pages);
    });
    return () => {
      live = false;
    };
  }, [src]);

  const previewPageCount = Math.min(pageCount, MAX_PREVIEW_PAGES);
  const truncatedPages = pageCount - previewPageCount;
  const mountedPages = useMemo(
    () => Array.from({ length: previewPageCount }, (_, i) => i + 1),
    [previewPageCount]
  );

  const goToPage = (target: number) =>
    setPage(Math.min(Math.max(target, 1), previewPageCount));

  // Show the requested page once its image has loaded; until then keep the
  // last loaded page on screen (under a spinner) so flipping never flashes.
  const displayPage = loadedPages.has(page) ? page : lastLoadedPageRef.current;
  useEffect(() => {
    if (loadedPages.has(page)) lastLoadedPageRef.current = page;
  }, [loadedPages, page]);
  const isPageLoading = pageCount > 1 && page !== displayPage;

  const markLoaded = (p: number) =>
    setLoadedPages((prev) => {
      if (prev.has(p)) return prev;
      const next = new Set(prev);
      next.add(p);
      return next;
    });

  const controlsVisibility = alwaysShowControls
    ? ""
    : "opacity-0 group-hover/paged-image:opacity-100 transition-opacity";
  const arrowSize = alwaysShowControls ? "btn-sm" : "btn-xs";

  if (pageCount <= 1) {
    return (
      <div className={`relative inline-block ${containerClassName}`}>
        <CldImage
          src={src}
          alt={alt}
          width={width}
          height={height}
          crop={crop}
          sizes={sizes}
          className={className}
          onClick={onClick}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative inline-block group/paged-image ${containerClassName}`}
    >
      {mountedPages.map((p) => (
        <CldImage
          key={p}
          src={src}
          alt={`${alt} (page ${p})`}
          width={width}
          height={height}
          crop={crop}
          sizes={sizes}
          rawTransformations={p > 1 ? [`pg_${p}`] : undefined}
          // Hidden preloads have no layout box, so the default lazy loading
          // would never fetch them.
          loading={p > 1 ? "eager" : undefined}
          className={`${className ?? ""} ${p === displayPage ? "" : "hidden"}`}
          onClick={onClick}
          onLoad={() => markLoaded(p)}
        />
      ))}
      {isPageLoading && (
        <span className="loading loading-spinner loading-sm absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"></span>
      )}
      <button
        type="button"
        aria-label="Previous page"
        className={`absolute left-1 top-1/2 -translate-y-1/2 btn btn-circle ${arrowSize} ${controlsVisibility}`}
        disabled={page <= 1}
        onClick={(e) => {
          e.stopPropagation();
          goToPage(page - 1);
        }}
      >
        &lsaquo;
      </button>
      <button
        type="button"
        aria-label="Next page"
        className={`absolute right-1 top-1/2 -translate-y-1/2 btn btn-circle ${arrowSize} ${controlsVisibility}`}
        disabled={page >= previewPageCount}
        onClick={(e) => {
          e.stopPropagation();
          goToPage(page + 1);
        }}
      >
        &rsaquo;
      </button>
      <span
        className={`absolute bottom-1 left-1/2 -translate-x-1/2 badge badge-sm badge-neutral whitespace-nowrap pointer-events-none ${controlsVisibility}`}
      >
        {truncatedPages > 0 && page === previewPageCount
          ? `${page} / ${pageCount} · +${truncatedPages} more`
          : `${page} / ${pageCount}`}
      </span>
    </div>
  );
};

export default PagedCloudImage;
